import React, {useState, useRef, useEffect, useContext} from 'react';
import Info from "../../Alerts/Info";
import { MediaRecorder, register } from 'extendable-media-recorder';
import { connect } from 'extendable-media-recorder-wav-encoder';
import filterResponse from "../../../filterResponse";

const AudioRecorder = ({ setOtherDisabledReason, onBlobReady, file }) => {
	const [isRecording, setIsRecording] = useState(false);
	const [elapsedMs, setElapsedMs] = useState(0);
	const [message, setMessage] = useState(null);
	const [disableRecording, setDisableRecording] = useState(false);
	const [isPaused, setIsPaused] = useState(false);

	const mediaRecorderRef = useRef(null);

	useEffect(() => {
		let isRecording = false;
		let key = null;
		let chain = Promise.resolve();

		(async () => {
			if (!mediaRecorderRef.current) {
				try {
					await register(await connect());
				}
				catch (e) {
					console.error(e);
				}
				const constraints = {
					audio: true
				};

				if (Number(file.properties.minSamplingRate) && Number(file.properties.minSamplingRate) === Number(file.properties.maxSamplingRate)) {
					constraints.audio = {
						sampleRate: Number(file.properties.minSamplingRate)
					};
				}

				try {
					const stream = await navigator.mediaDevices.getUserMedia(constraints);
					const sampleRate = stream.getAudioTracks()[0].getSettings().sampleRate;

					if (file.properties.minSamplingRate) {
						if (sampleRate < Number(file.properties.minSamplingRate)) {
							setDisableRecording(true);
							setMessage("You are not allowed to record because the minimum sample rate is: " + file.properties.minSamplingRate + ' Hz');
						}
						else {
							setDisableRecording(false);
						}
					}
					if (file.properties.maxSamplingRate) {
						if (sampleRate > Number(file.properties.maxSamplingRate)) {
							setDisableRecording(true);
							setMessage("You are not allowed to record because the maximum sample rate is: " + file.properties.maxSamplingRate + ' Hz');
						}
						else {
							setDisableRecording(false);
						}
					}

					mediaRecorderRef.current = new MediaRecorder(stream, {
						mimeType: 'audio/wav',
					});
				}
				catch (e) {
					if (e.name === 'NotAllowedError') {
						setMessage("Please allow microphone access in order to record audio.");
					}
					console.error(e);
				}
			}

			mediaRecorderRef.current?.addEventListener('dataavailable', onDataAvailable);
			mediaRecorderRef.current?.addEventListener('stop', onStop);
			mediaRecorderRef.current?.addEventListener('error', onError);
			mediaRecorderRef.current?.addEventListener('start', onStart);
			mediaRecorderRef.current?.addEventListener('pause', onPause);
			mediaRecorderRef.current?.addEventListener('resume', onResume);
		})();

		let intervalId;

		async function onStop () {
			clearInterval(intervalId);
			intervalId = null;
			setIsRecording(false);
			setElapsedMs(0);
			setIsPaused(false);
			await chain;
			await onBlobReady(key);
			key = null;
		}

		function onStart() {
			startTimer();
			isRecording = true; // used for internal use
			setIsRecording(true);
		}

		function startTimer() {
			intervalId = setInterval(() => {
				setElapsedMs(prevMs => prevMs + 300);
			}, 300);
		}

		function onError(event) {
			console.error("MediaRecorder error:", event.error);
			stopRecording();
		}

		function onPause() {
			setIsPaused(true);
			clearInterval(intervalId);
		}

		function onResume() {
			setIsPaused(false);
			startTimer();
		}

		async function onDataAvailable(evt) {
			chain = chain.then(() => { // so that they are added in sequence in case of internet slowness
				return uploadChunk(evt);
			});
		}

		async function uploadChunk(evt) {
			try {
				const formData = new FormData();

				formData.append('chunk', evt.data);
				key && formData.append('key', key); // to append to the file

				const response = await fetch(process.env.REACT_APP_ENDPOINT + '/files/append', {
					method: "POST",
					credentials: "include",
					body: formData,
					redirect: "follow"
				});
				await filterResponse(response);

				if (response.status === 201) {
					const {key: keyToSet} = await response.json();
					key = keyToSet;
				}
				else {
					throw new Error("Wrong status code");
				}
			}
			catch (e) {
				console.error(e);
			}
		}

		return () => {
			mediaRecorderRef.current?.removeEventListener("dataavailable", onDataAvailable);
			mediaRecorderRef.current?.removeEventListener("stop", onStop);
			mediaRecorderRef.current?.removeEventListener("start", onStart);
			mediaRecorderRef.current?.removeEventListener('error', onError);
			mediaRecorderRef.current?.removeEventListener('pause', onPause);
			mediaRecorderRef.current?.removeEventListener('resume', onResume);

			intervalId && clearInterval(intervalId);
		}
	}, []);

	useEffect(() => {
		if (elapsedMs / 1000 >= Number(file.properties.maxAudioLength)) {
			stopRecording();
			setMessage(`Your recording has been stopped because it has reached the maximum of ${file.properties.maxAudioLength} seconds.`);
		}
	}, [elapsedMs])

	useEffect(() => {
		setOtherDisabledReason(isRecording);
	}, [isRecording]);

	const startRecording = () => {
		try {
			mediaRecorderRef.current.start(5000); // save every 10 seconds
		}
		catch (err) {
			console.error("Failed to get user media:", err);
		}
	};

	const stopRecording = () => {
		mediaRecorderRef.current.stop();
	};

	// Format time based on elapsed milliseconds, rounding seconds up
	const formatTime = (milliseconds) => {
		const totalSeconds = Math.ceil(milliseconds / 1000); // Round up seconds
		const mins = Math.floor(totalSeconds / 60);
		const secs = totalSeconds % 60;
		return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
	};

	function changeRecordingState () {
		if (mediaRecorderRef.current.state === "paused") {
			mediaRecorderRef.current.resume();
		}
		else {
			mediaRecorderRef.current.pause();
		}
	}

	return (
		<div>
			{message && <Info message={message} />}
			{isRecording ?
				<div className={"mb-2"}>
					<button className={"btn btn-sm btn-danger"} disabled={elapsedMs / 1000 < file.properties.minAudioLength} type="button" onClick={stopRecording}>
						Stop Recording
					</button>
					<button className={"btn btn-secondary btn-sm ms-1"} type={"button"} onClick={changeRecordingState}>{isPaused ? 'Resume': 'Pause'}</button>
				</div>

				:
				<button className={"btn btn-sm btn-success mb-2"} type="button" disabled={disableRecording} onClick={startRecording}>
					Start Recording
				</button>
			}

			<span className="ms-2">{formatTime(elapsedMs)}</span>
		</div>
	);
};

export default AudioRecorder;
